import React, { useState, useEffect } from 'react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { Box } from '@mui/material';
import Loader from './components/common/custom-component/Loader';
import { getKeycloakInstance } from './keycloak';
import { logger } from 'helpers/logger';
import { storeKeycloakOfflineToken, getStoredOfflineToken } from './helpers/authenticatedRequest';
import store from './store';
import { triggerOutboxReplay } from './store/slices/offlineSlice';
import { db } from './indexedDB';

export const OfflineAuthContext = React.createContext(null);

const KeycloakLoadingComponent = () => {
    if (window.parent === window) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: 2,
            }}>
                <Loader variant="circular" message="Loading..." size="small" />
            </Box>
        );
    }
};

export default function KeycloakWrapper({ children, domainConfig }) {
    const [isOfflineMode, setIsOfflineMode] = useState(
        () => !navigator.onLine && !!getStoredOfflineToken()
    );

    // When connection is restored, switch back to full Keycloak auth
    useEffect(() => {
        const handleOnline = () => setIsOfflineMode(false);
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, []);

    // Always create the Keycloak instance (constructor only — no network calls)
    const { keycloak, initOptions } = getKeycloakInstance({
        url: domainConfig.keycloakBaseUrl,
        realm: domainConfig.keycloakRealm,
        clientId: domainConfig.keycloakClientId,
    });

    const handleEvent = (event, error) => {
        logger.info('🔑 Keycloak event:', event);

        if (event === 'onAuthSuccess' || event === 'onAuthRefreshSuccess') {
            storeKeycloakOfflineToken(keycloak);
        }

        if (event === 'onAuthSuccess') {
            // Only trigger replay on initial auth — not on every token refresh
            // to avoid an infinite loop (replay refreshes token → onAuthRefreshSuccess → replay again)
            Promise.all([db.outbox.count(), db.action_queue.count()])
                .then(([outbox, actionQueue]) => {
                    if (outbox + actionQueue > 0) {
                        store.dispatch(triggerOutboxReplay());
                    }
                });
        }

        if (event === 'onInitError') {
            logger.error('❌ Keycloak init failed:', error);
            // Only fall back to offline mode when the device is actually offline.
            // Keycloak can fire onInitError for other reasons (e.g. iframe timeout
            // due to browser third-party cookie restrictions) even while online.
            if (!navigator.onLine && getStoredOfflineToken()) {
                setIsOfflineMode(true);
            }
        }

        if (error) {
            logger.error(`🔑 Keycloak error on ${event}:`, error);
        }
    };

    if (isOfflineMode) {
        return (
            <OfflineAuthContext.Provider value={{ isOfflineAuth: true }}>
                {children}
            </OfflineAuthContext.Provider>
        );
    }

    return (
        <ReactKeycloakProvider
            authClient={keycloak}
            initOptions={initOptions}
            LoadingComponent={<KeycloakLoadingComponent />}
            onEvent={handleEvent}
        >
            {children}
        </ReactKeycloakProvider>
    );
}
